import React from "react";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type NumberArguments = {
  label: string;
  value: string;
};

interface Props {
  element: FormElement<NumberArguments>;
}

const FormNumber: React.FC<Props> = observer(({ element }) => {
  const editing = useEditing(element);
  const { classes } = useStyles();

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.longTextInputContent}>
            <Typography
              variant="h6"
              color="textPrimary"
              style={{ marginBottom: 12 }}
            >
              {element.arguments.label || "Number label"}
            </Typography>
            <TextField
              fullWidth={true}
              defaultValue={element.arguments.value}
              type="number"
              disabled
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Enter number label
              </Typography>
              <TextField
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />

              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Default value
              </Typography>
              <TextField
                fullWidth
                type="number"
                InputLabelProps={{
                  shrink: true,
                }}
                value={element.arguments.value}
                onChange={(e) => element.setArgument("value", e.target.value)}
              />
              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormNumber;
